import { useUserState } from '../../redux/reducers/userReducer';
import { useAccountState } from '../../redux/reducers/accountReducer';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef, useEffect, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    Input,
    Label,
    Link,
    Persona,
    SelectTabData,
    SelectTabEvent,
    Spinner,
    Switch,
    Tab,
    TabList,
    Text,
    Textarea,
    ToggleButton,
    Toolbar,
    ToolbarDivider,
    Tooltip,
    makeStyles,
    mergeClasses,
    tokens,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { addYears } from '@fluentui/react-calendar-compat';
import {
    ArrowResetRegular,
    ArrowUpload16Regular,
    bundleIcon,
    DeleteFilled,
    DeleteRegular,
    EditFilled,
    EditRegular,
    InfoRegular,
    MoneyFilled,
    MoneyRegular,
    TaskListAddRegular,
} from '@fluentui/react-icons';
import { Column, Paths, Row } from '../common';
import { CaseDropdown } from '../controls/Comboboxes/CaseDropdown';
import {
    CaseDocument,
    CaseMilestone,
    Note,
    Task,
    TimeTrackingEntry,
    useInitialValues,
    User,
    UserAccount,
    UserRef,
} from '../../data/types';
import { useCaseDocumentService, useCaseMilestoneService, useCaseNoteService, useCaseService, useTimeTrackingService } from '../../data/services';
import { useActiveTimer } from '../../data/services/useActiveTimer';
import { useAccess } from '../../auth/useAccess';
import { useUtilities } from '../../utilities/useUtilities';
import { useNavigate } from 'react-router';
import { CaseNoteForm } from '../controls/CaseNoteForm';
import { NoteControl } from '../controls/NoteControl';
import { useCommonStyles } from '../common/Styles/commonStyles';
import { FormikProps, useFormik } from 'formik';
import { MilestoneDropdown } from '../controls/Comboboxes/MilestoneDropdown';
import { useValidation } from '../../data/types/validation';
import { UserDropdown } from '../controls/Comboboxes/UserDropdown';
import { useMediaSizes } from '../common/Styles/useMediaSizes';
import { EditableLabel } from '../controls/EditableLabel';
import { appTokens } from '../common/Styles/appTokens';
import { PersonControl } from '../controls/PersonControl';
import { Section } from '../common/Section';
import { ConfirmationDialog } from './ConfirmationDialog';
import { useDialogControls } from './useDialogControls';
import { DocumentTable } from '../pages/Cases/CaseDocuments/DocumentTable';
import { DocumentDrawer } from '../pages/Cases/CaseDocuments/DocumentDrawer';
import { CustomDataTable, CustomDataTableAction, CustomDataTableColumn } from '../controls/CustomDataTable/CustomDataTable';
import { AddEditTimeTrackEntryDialog } from './AddEditTimeTrackEntryDialog';
import FileUploadManager from '../../data/services/FileUploadManager';
import { useToasts } from '../toasts/useToasts';
import { useCaseDocumentActions } from '../../redux/actions/caseDocumentActions';
import { useCustomDataTableFeatures } from '../controls/CustomDataTable/useCustomDataTableFeatures';
import { CaseTabs } from '../pages/Tabs/CaseTabs';

const useStyles = makeStyles({
    dialog: {
        border: `1px solid ${tokens.colorBrandStroke1}`,
        maxWidth: '75vw',
        height: '75vh',
    },
    dialogLaptopScreen: {
        maxWidth: '80vw',
    },
    dialogUnderLaptopScreen: {
        maxWidth: '85vw',
    },
    casePersona: {
        userSelect: 'none',
        cursor: 'pointer',
    },
    billableBtn: {
        ':disabled': {
            //border: '1px solid green'
            color: '#333',
        },
    },
    title: {
        fontSize: '1.25rem',
    },
    documentsContentArea: {
        position: 'relative',
        minHeight: 0,
    },
    documentsDragArea: {
        position: 'absolute',
        inset: '.5rem',
        borderRadius: tokens.borderRadiusLarge,
        backgroundColor: tokens.colorBrandBackground2,
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        textAlign: 'center',
        opacity: '0',
    },
    documentsDragActive: {
        border: `3px solid ${tokens.colorBrandBackground4Static}`,
        opacity: '.4',
        zIndex: tokens.zIndexOverlay,
    },
    documentsBtn: {
        marginInline: '.25rem',
    },
    documentsUploadBtn: {
        marginBlock: '.25rem',
    },
    documentsDrawer: {
        width: '32rem',
    },
});

enum TaskDialogTabs {
    details = 'Details',
    discussion = 'Discussion',
    documents = 'Documents',
    time = 'Time',
}

const MAX_FILES_PER_DROP = 20;
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024 * 1024; // 50 GB
const ACCEPTED_DOCUMENT_TYPES: Record<string, string[]> = {
    'application/pdf': ['.pdf'],
    'text/plain': [
        '.txt',
        '.log',
        '.md',
        '.markdown',
        '.rtf',
        '.ini',
        '.cfg',
        '.yaml',
        '.yml',
        '.xml',
        '.html',
        '.htm',
        '.css',
        '.js',
        '.ts',
        '.tsx',
        '.sql',
    ],
    'text/csv': ['.csv'],
    'application/json': ['.json'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'application/zip': ['.zip'],
    'application/x-zip-compressed': ['.zip'],
    'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.tiff'],
};

type AddEditTaskDialogProps = {
    onSubmit?: (task: Task, cache?: boolean) => Promise<void>;
    children?: JSX.Element;
    initialCaseId?: string;
    canChangeCase?: boolean;
    billableOnly?: boolean;
    taskToEdit?: Task;
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    milestone?: CaseMilestone;
    selectMilestone?: boolean;
};
export const AddEditTaskDialog = forwardRef((props: AddEditTaskDialogProps, _ref) => {
    const {
        children,
        onSubmit,
        initialCaseId,
        billableOnly = false,
        taskToEdit,
        open,
        onOpenChange,
        milestone,
        selectMilestone = true,
    } = props;

    //Hooks
    const { primaryAccount } = useAccountState();
    const { users } = useUserState();
    const { user } = (primaryAccount as UserAccount) ?? {};
    const access = useAccess(primaryAccount?.user);
    const initValues = useInitialValues();
    const caseService = useCaseService();
    const initialCase = caseService.findCaseById(initialCaseId as string);
    const caseNoteService = useCaseNoteService(taskToEdit?.caseId as string);
    const milestoneService = useCaseMilestoneService(initialCaseId as string);
    const mediaSizes = useMediaSizes();
    const utils = useUtilities();
    const navTo = useNavigate();
    const { validateTask } = useValidation();
    const timeTrackingService = useTimeTrackingService();
    const toasts = useToasts();
    
    //State
    const [notesToAddOnSave, setNotesToAddOnSave] = useState<Note[]>([]);
    const [loadedTaskNotes, setLoadedTaskNotes] = useState<Note[]>([]);
    const [dueDateMin, setDueDateMin] = useState<Date>(
        !!initialCase?.retainedDate ? new Date(initialCase.retainedDate) : addYears(new Date(), -1),
    );
    const [milestones, setMilestones] = useState<CaseMilestone[]>([]);
    const [tab, setTab] = useState<TaskDialogTabs>(TaskDialogTabs.details);
    const [taskTimeEntries, setTaskTimeEntries] = useState<TimeTrackingEntry[]>([]);
    const [timeEntriesLoading, setTimeEntriesLoading] = useState<boolean>(false);
    const [timeEntriesError, setTimeEntriesError] = useState<boolean>(false);
    
    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const dialogSurfaceClasses = mergeClasses(
        styles.dialog,
        mediaSizes.isUnderDesktop && styles.dialogLaptopScreen,
        mediaSizes.isUnderLaptop && styles.dialogUnderLaptopScreen,
    );
    const billableBtnClasses = mergeClasses(commonStyles.button.root, styles.billableBtn);
    
    //Dialog Control
    const onCloseDialog = () => {
        taskForm.resetForm({ values: initTask() });
    };
    const [dialogOpen, onOpenChangeHandler, closeDialog] = useDialogControls(open as boolean, onOpenChange, onCloseDialog);
    
    //Form
    const initTask = (): Task => {
        if (!!taskToEdit) {
            return {
                ...taskToEdit,
                dueBy: utils.date.validDate(taskToEdit?.dueBy),
            };
        } else {
            return {
                ...initValues.task(user.userId, initialCaseId),
                milestoneId: milestone?.milestoneId,
                dueBy: utils.date.validDate(milestone?.dueDate),
            };
        }
    };
    const taskForm = useFormik<Task>({
        initialValues: initTask(),
        onSubmit: async (values, { resetForm }) => {
            const task: Task =
                typeof structuredClone === 'function' ? (structuredClone(values) as Task) : ({ ...values } as Task);

            if (!!billableOnly) {
                task.billable = true;
            }
            if (milestone) {
                task.milestoneId = milestone.milestoneId;
            }

            const saveOperations: Promise<unknown>[] = [];
            setTab(TaskDialogTabs.details);

            //Save Notes
            if (!!notesToAddOnSave.length) {
                const updatedNotes: Note[] = notesToAddOnSave.map((n) => ({
                    ...n,
                    caseId: task.caseId,
                    taskId: task.taskId,
                }));
                saveOperations.push(caseNoteService.upsertCaseNotesAsync(updatedNotes));
            }

            if (!!onSubmit && taskForm.dirty) {
                saveOperations.push(onSubmit(task));
            }

            closeDialog();

            await Promise.all(saveOperations);

            if (!!taskToEdit) {
                resetForm({ values });
            } else {
                resetForm({ values: initTask() });
            }
        },
        onReset: () => {
            setNotesToAddOnSave([]);
            setTab(TaskDialogTabs.details);
            setDueDateMin(addYears(new Date(), -1));
        },
        validate: (values) => {
            const errors: Record<string, string> = {};

            if (primaryAccount?.account.settings?.requireTasksBeTiedToMilestones && values.caseId && !values.milestoneId) {
                errors.milestoneId = 'Milestone is required';
            }

            if (values.dueBy && !utils.date.validDate(values.dueBy)) {
                errors.dueBy = 'Due by date is invalid';
            }
            if (values.dueBy && values.dueBy < dueDateMin) {
                errors.dueBy = 'Due by date cannot be earlier than the retained date of the case.';
            }
            return errors;
        },
        validateOnBlur: false,
        validateOnChange: true,
        validationSchema: validateTask.schema,
    });
    const documentService = useCaseDocumentService(taskForm.values.caseId || '');

    //Access
    const canEditTask = () => {
        return !!taskToEdit && access.cases.canEditTasks(taskForm.values.caseId);
    };
    const canCreateTask = () => {
        return !taskToEdit && access.cases.canCreateTasks();
    };
    const canCreateTasksForOthers = () => {
        return !!access.cases.canCreateTasksForOthers();
    };
    const canAddNotes = () => {
        return !!access.cases.canAddNotes(taskToEdit?.caseId);
    };
    const canEditDocs = () => {
        return !!access.cases.canEditDocuments(taskForm.values.caseId);
    };
    const canDeleteDocs = () => {
        return !!access.cases.canDeleteDocuments(taskForm.values.caseId);
    };
    const canUploadDocs = () => {
        return !!access.cases.canUploadDocuments(taskForm.values.caseId);
    };
    const canViewTrackedTime = () => {
        return !!primaryAccount?.account.features?.timeTracking && !!taskToEdit?.taskId && !!taskToEdit?.caseId;
    };
    const canEditTimeEntry = (entry: TimeTrackingEntry) => {
        return !entry.invoiceId && entry.userId === user?.userId;
    };
    const canDeleteTimeEntry = (entry: TimeTrackingEntry) => {
        return !entry.invoiceId && (entry.userId === user?.userId || !!access.account.isAccountAdmin());
    };

    //Logic
    const getDialogTitle = () => {
        if (!taskToEdit) {
            return 'Add Task';
        } else {
            return `${access.cases.canEditTasks(taskForm.values.caseId) ? 'View / Edit' : 'View'} ${!!billableOnly ? 'Billable ' : ''}Task`;
        }
    };
    const loadNotes = async () => {
        if (!!taskToEdit && caseNoteService.notes?.length === 0) {
            try {
                const taskNotes = await caseNoteService.getCaseNotesForTaskAsync(taskToEdit?.taskId as string);
                setLoadedTaskNotes(taskNotes);
            } catch {
                //nom nom nom
            }
        }
    };
    const getMilestones = async (msCaseId: string) => {
        const cached = milestoneService.milestones
            .filter((ms) => ms.caseId === msCaseId)
            .sort((a, b) => (a.sortKey ?? 0) - (b.sortKey ?? 0));
        if (cached.length) return cached;

        return milestoneService.getCaseMilestonesAsync(msCaseId);
    };
    const getAssignedUser = (userId: string) => {
        return users.find((u) => u.userId === userId);
    };
    const getMilestone = (msId: string) => {
        return milestones.find((ms) => ms.milestoneId === msId);
    };
    const loadTimeEntries = async () => {
        setTimeEntriesLoading(true);
        setTimeEntriesError(false);
        try {
            const entries = await timeTrackingService.getTimeEntriesForTaskAsync(
                taskToEdit?.caseId as string,
                taskToEdit?.taskId as string,
            );
            setTaskTimeEntries(entries);
        } catch {
            setTaskTimeEntries([]);
            setTimeEntriesError(true);
        } finally {
            setTimeEntriesLoading(false);
        }
    };
    const modifyTimeEntry = async (entry: TimeTrackingEntry) => {
        try {
            await timeTrackingService.upsertTimeEntry(entry);
            setTaskTimeEntries((prev) => prev.map((e) => (e.entryId === entry.entryId ? entry : e)));
        } catch {
            toasts.notifyError('Time entry not saved', 'The time entry could not be updated. Please try again.');
        }
    };
    const deleteTimeEntry = async (entry: TimeTrackingEntry) => {
        try {
            await timeTrackingService.deleteTimeEntryAsync(entry);
            setTaskTimeEntries((prev) => prev.filter((e) => e.entryId !== entry.entryId));
        } catch {
            toasts.notifyError('Time entry not deleted', 'The time entry could not be deleted. Please try again.');
        }
    };

    //Notes
    const filterNotesForTask = (note: Note) => {
        return note.taskId === taskForm.values.taskId;
    };
    const getNotes = () => {
        const existingNotes = [...(caseNoteService.notes || loadedTaskNotes)].filter(filterNotesForTask);
        const notesForMilestone = [...existingNotes, ...notesToAddOnSave];
        return notesForMilestone;
    };
    const onSubmitNote = async (note: Note) => {
        setNotesToAddOnSave((prev) => {
            return [...prev, note];
        });
    };

    //UI Events
    const onTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
        setTab(data.value as TaskDialogTabs);
    };
    const onSelectDate = (date: Date | null | undefined) => {
        taskForm.setFieldValue('dueBy', date || undefined);
    };
    const onMilestoneSelected = (msId?: string) => {
        taskForm.setFieldValue('milestoneId', msId);

        //New tasks inherit the milestone's due date unless the user already picked one
        if (!taskToEdit && !taskForm.values.dueBy && !!msId) {
            const msDueDate = utils.date.validDate(getMilestone(msId)?.dueDate);
            if (!!msDueDate) {
                taskForm.setFieldValue('dueBy', msDueDate);
            }
        }
    };
    const onUserSelect = (selUser?: User | UserRef) => {
        taskForm.setFieldValue('userId', selUser?.userId);
        taskForm.setFieldValue('username', selUser?.username);
    };
    
    const clickSave = async (e: any) => {
        e.stopPropagation();

        await taskForm.submitForm();
    };
    const clickCancel = (e: any) => {
        e.stopPropagation();
        taskForm.resetForm({ values: initTask() });
        setNotesToAddOnSave([]);
        closeDialog();
    };
    const clickCaseLink = (e: any) => {
        e.stopPropagation();

        navTo(Paths.viewEditCase(taskForm.values.caseId as string));
    };
    const clickMilestonesLink = (e: any) => {
        e.stopPropagation();

        closeDialog();
        navTo(Paths.viewEditCase(taskForm.values.caseId as string), { state: { tab: CaseTabs.milestones } });
    };
    const clickBillableButton = (e: any) => {
        e.stopPropagation();

        taskForm.setFieldValue('billable', !taskForm.values.billable);
    };

    //Icons
    const MoneyIcons = bundleIcon(MoneyFilled, MoneyRegular);

    //Effects
    useEffect(() => {
        if (!dialogOpen) return;

        if (!!taskForm.values.caseId) {
            getMilestones(taskForm.values.caseId)?.then((ms) => setMilestones(ms));
        } else {
            setMilestones([]);
        }
    }, [dialogOpen, taskForm.values.caseId, milestone?.milestoneId]);

    useEffect(() => {
        if (!dialogOpen) return;

        taskForm.resetForm({ values: initTask() });

        if (!!dialogOpen && !!taskToEdit) {
            loadNotes();
        }
    }, [dialogOpen, !!taskToEdit]);

    useEffect(() => {
        if (!dialogOpen || !taskForm.values.caseId) {
            return;
        }

        documentService.loadDocumentsAsync().catch(() => {
            // Keep dialog usable even when docs fail to load.
        });
    }, [dialogOpen, taskForm.values.caseId]);

    useEffect(() => {
        if (!dialogOpen || tab !== TaskDialogTabs.time || !canViewTrackedTime()) {
            return;
        }

        loadTimeEntries();
    }, [dialogOpen, tab, taskToEdit?.taskId]);

    useEffect(() => {
        if (!dialogOpen) {
            setTaskTimeEntries([]);
            setTimeEntriesError(false);
        }
    }, [dialogOpen]);

    return (
        <Dialog open={dialogOpen} onOpenChange={onOpenChangeHandler}>
            <DialogTrigger disableButtonEnhancement>{children}</DialogTrigger>
            <DialogSurface
                className={dialogSurfaceClasses}
                onClick={(e) => {
                    e.stopPropagation();
                }}
                onDoubleClick={(e) => {
                    e.stopPropagation();
                }}
            >
                <DialogBody style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <DialogTitle>
                        <Column noGap>
                            <Row>
                                <Column gap='small'>
                                    <Row gap='small'>
                                        <Column width='auto' vAlign='middle'>
                                            <TaskListAddRegular />
                                        </Column>
                                        <Column>
                                            <Text size={300}>{getDialogTitle()}</Text>
                                        </Column>
                                    </Row>
                                    <Field validationMessage={taskForm.errors.title}>
                                        <EditableLabel
                                            labelClassName={styles.title}
                                            name='title'
                                            value={taskForm.values.title || ''}
                                            onChange={taskForm?.handleChange}
                                            placeholder='Enter Task Title'
                                        />
                                    </Field>
                                </Column>
                                <Column width='auto' gap='medium' align='right' vAlign='top' padding='pr-2'>
                                    {!!taskForm.values.caseId && (
                                        <Persona
                                            onClick={clickCaseLink}
                                            className={styles.casePersona}
                                            name={caseService.findCaseById(taskForm.values.caseId)?.caseName}
                                            size='extra-small'
                                            textAlignment='center'
                                            avatar={{ color: 'colorful', shape: 'square' }}
                                            data-testid='task-dialog-case-persona'
                                        />
                                    )}
                                    {!!taskForm.values.milestoneId && (
                                        <Text weight='semibold'>
                                            Milestone: <Text>{getMilestone(taskForm.values.milestoneId)?.name}</Text>
                                        </Text>
                                    )}
                                </Column>
                            </Row>
                            <Row padding='pt-2'>
                                <Column vAlign='middle'>
                                    <Row>
                                        <Column width='auto' vAlign='middle'>
                                            <Text size={300} weight='semibold'>
                                                Assigned To:
                                            </Text>
                                        </Column>
                                        <Column vAlign='middle'>
                                            {canCreateTasksForOthers() ? (
                                                <Column align='left' width='auto'>
                                                    <Field required validationMessage={taskForm.errors.userId}>
                                                        <UserDropdown
                                                            initialUserId={taskToEdit?.userId || user.userId}
                                                            caseId={taskForm.values.caseId}
                                                            onUserSelected={onUserSelect}
                                                            appearance='outline'
                                                        />
                                                    </Field>
                                                </Column>
                                            ) : (
                                                <PersonControl
                                                    person={getAssignedUser(taskToEdit?.userId || user.userId)}
                                                    size='option'
                                                />
                                            )}
                                        </Column>
                                    </Row>
                                </Column>
                                <Column align='right' vAlign='middle'>
                                    <ToggleButton
                                        className={billableBtnClasses}
                                        name='billable'
                                        appearance={billableOnly || taskForm.values.billable ? 'primary' : 'secondary'}
                                        checked={billableOnly || taskForm.values.billable}
                                        onChange={taskForm.handleChange}
                                        onClick={clickBillableButton}
                                        icon={<MoneyIcons />}
                                        size='small'
                                        disabled={!canEditTask() && !canCreateTask()}
                                        data-testid='task-dialog-billable-button'
                                    >
                                        Billable
                                        <Checkbox
                                            name='billable'
                                            className={commonStyles.button.root}
                                            labelPosition='before'
                                            checked={billableOnly || taskForm.values.billable}
                                            onChange={taskForm.handleChange}
                                            readOnly={!canEditTask() && !canCreateTask()}
                                        />
                                    </ToggleButton>
                                </Column>
                            </Row>
                        </Column>
                    </DialogTitle>
                    <DialogContent style={{ height: '100%' }}>
                        <Column flex='1' gap='small' padding='pr-2' overflowY='clip'>
                            <Column
                                height='auto'
                                padding='pb-2'
                                style={{ borderBlockEnd: `1px solid ${appTokens.text.colors.subtle}` }}
                            >
                                <TabList selectedValue={tab} onTabSelect={onTabSelect}>
                                    <Tab value={TaskDialogTabs.details}>Details</Tab>
                                    <Tab value={TaskDialogTabs.discussion}>Discussion</Tab>
                                    <Tab value={TaskDialogTabs.documents}>Documents</Tab>
                                    {canViewTrackedTime() && (
                                        <Tab value={TaskDialogTabs.time} data-testid='task-dialog-time-tab'>
                                            Time
                                        </Tab>
                                    )}
                                </TabList>
                            </Column>

                            {tab == TaskDialogTabs.details && (
                                <DetailsTab
                                    taskForm={taskForm}
                                    taskToEdit={taskToEdit}
                                    canCreateTask={canCreateTask()}
                                    canEditTask={canEditTask()}
                                    initialCaseId={initialCaseId}
                                    requireMilestone={primaryAccount?.account.settings?.requireTasksBeTiedToMilestones}
                                    selectMilestone={selectMilestone}
                                    initialMilestoneId={milestone?.milestoneId || taskForm.values.milestoneId || ''}
                                    milestones={milestones}
                                    onMilestoneSelected={onMilestoneSelected}
                                    onClickMilestonesLink={clickMilestonesLink}
                                    dueDateMin={dueDateMin}
                                    onSelectDate={onSelectDate}
                                />
                            )}

                            {tab == TaskDialogTabs.discussion && (
                                <NotesTab
                                    caseId={taskForm.values.caseId!}
                                    canAddNotes={canAddNotes()}
                                    notes={getNotes()}
                                    onSubmitNote={onSubmitNote}
                                />
                            )}

                            {tab == TaskDialogTabs.documents && (
                                <DocumentsTab
                                    caseId={taskForm.values.caseId}
                                    taskObj={taskForm.values}
                                    accountId={primaryAccount?.account.accountId}
                                    docs={documentService.docs || []}
                                    canEditDocs={canEditDocs()}
                                    canDeleteDocs={canDeleteDocs()}
                                    canUploadDocs={canUploadDocs()}
                                    onDeleteDocs={documentService.deleteCaseDocumentsAsync}
                                    onModifyDocument={documentService.upsertCaseDocumentAsync}
                                />
                            )}

                            {tab == TaskDialogTabs.time && canViewTrackedTime() && (
                                <TimeTab
                                    entries={taskTimeEntries}
                                    loading={timeEntriesLoading}
                                    hasError={timeEntriesError}
                                    accountUsers={primaryAccount?.accountUsers}
                                    canEditEntry={canEditTimeEntry}
                                    canDeleteEntry={canDeleteTimeEntry}
                                    onModifyEntry={modifyTimeEntry}
                                    onDeleteEntry={deleteTimeEntry}
                                />
                            )}
                        </Column>
                        {/* <Text>{taskForm.errors ? JSON.stringify(taskForm.errors) : ''}</Text> */}
                    </DialogContent>
                    <DialogActions fluid style={{ justifyContent: 'flex-end' }}>
                        {(canCreateTask() || canEditTask()) && (
                            <Button
                                className={commonStyles.button.root}
                                type='submit'
                                appearance='primary'
                                disabled={
                                    taskForm.isSubmitting ||
                                    !taskForm.isValid ||
                                    (!canEditTask() && !canCreateTask()) ||
                                    (!taskForm.dirty && notesToAddOnSave.length === 0)
                                }
                                onClick={clickSave}
                                data-testid='task-dialog-save-button'
                            >
                                Save
                            </Button>
                        )}
                        <DialogTrigger disableButtonEnhancement>
                            {!!taskForm.dirty || notesToAddOnSave.length > 0 ? (
                                <ConfirmationDialog
                                    icon={<ArrowResetRegular />}
                                    title='Discard changes?'
                                    text='You have unsaved changes. Are you sure you want to discard them?'
                                    onConfirm={clickCancel}
                                >
                                    <Button
                                        appearance='outline'
                                        className={commonStyles.button.root}
                                        data-testid='task-dialog-cancel-button'
                                    >
                                        Cancel
                                    </Button>
                                </ConfirmationDialog>
                            ) : (
                                <Button
                                    appearance='secondary'
                                    className={commonStyles.button.root}
                                    onClick={clickCancel}
                                    data-testid='task-dialog-cancel-button'
                                >
                                    Close
                                </Button>
                            )}
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
});

type DetailsTabProps = {
    taskForm: FormikProps<Task>;
    taskToEdit?: Task;
    canCreateTask?: boolean;
    canEditTask?: boolean;
    initialCaseId?: string;
    requireMilestone?: boolean;
    selectMilestone?: boolean;
    initialMilestoneId?: string;
    milestones?: CaseMilestone[];
    onMilestoneSelected?: (msId?: string) => void;
    onClickMilestonesLink?: (e: any) => void;
    dueDateMin?: Date;
    onSelectDate?: (date: Date | null | undefined) => void;
};
const DetailsTab = ({
    taskForm,
    taskToEdit,
    canCreateTask,
    canEditTask,
    initialCaseId,
    requireMilestone,
    selectMilestone,
    milestones = [],
    onMilestoneSelected,
    onClickMilestonesLink,
    dueDateMin,
    onSelectDate,
}: DetailsTabProps) => {
    const { validateTask } = useValidation();

    const commonStyles = useCommonStyles();

    return (
        <Column flex='1' height='100%' padding='py-2' className={commonStyles.scroll.classes.scrollbarClasses} scroll>
            <Row>
                <Column flex='2'>
                    <Section title='Details'>
                        <Field
                            label='Description'
                            required
                            validationMessage={taskForm.errors.text}
                            hint={`${taskForm.values.text?.length || 0} / ${validateTask.text.maxLength}`}
                        >
                            <Textarea
                                name='text'
                                tabIndex={3}
                                rows={!!taskToEdit ? 5 : 3}
                                value={taskForm.values.text || ''}
                                onChange={taskForm.handleChange}
                                readOnly={!canEditTask && !canCreateTask}
                                appearance={!canEditTask && !canCreateTask ? 'outline' : undefined}
                                data-testid='task-dialog-textarea'
                                maxLength={validateTask.text.maxLength}
                            />
                        </Field>

                        <Field
                            label='Results'
                            validationMessage={taskForm.errors.results}
                            hint={`${taskForm.values.results?.length || 0} / ${validateTask.results.maxLength}`}
                        >
                            <Textarea
                                name='results'
                                tabIndex={3}
                                rows={!!taskToEdit ? 5 : 3}
                                value={taskForm.values.results || ''}
                                onChange={taskForm.handleChange}
                                readOnly={!canEditTask && !canCreateTask}
                                appearance={!canEditTask && !canCreateTask ? 'outline' : undefined}
                                data-testid='task-dialog-results'
                                maxLength={validateTask.results.maxLength}
                            />
                        </Field>
                    </Section>
                </Column>
                <Column flex='1'>
                    <Section title='Deadline'>
                        {(canEditTask || canCreateTask) && (
                            <Field label='Due By' validationMessage={taskForm.errors.dueBy}>
                                <DatePicker
                                    name='completeBy'
                                    value={taskForm.values.dueBy}
                                    onSelectDate={onSelectDate}
                                    placeholder='Select a date...'
                                    minDate={dueDateMin}
                                    data-testid='task-dialog-datepicker'
                                />
                            </Field>
                        )}
                    </Section>
                    <Section title='Planning'>
                        <Field label='Category' validationMessage={taskForm.errors.category}>
                            <Input
                                name='category'
                                value={taskForm.values.category || ''}
                                onChange={taskForm.handleChange}
                                readOnly={!canEditTask && !canCreateTask}
                                appearance={!canEditTask && !canCreateTask ? 'outline' : undefined}
                                data-testid='task-dialog-category'
                            />
                        </Field>
                    </Section>

                    <Section title='Effort'>
                        <Field label='Estimated Hours' validationMessage={taskForm.errors.estimatedHours}>
                            <Input
                                name='estimatedHours'
                                value={taskForm.values.estimatedHours || ''}
                                onChange={taskForm.handleChange}
                                readOnly={!canEditTask && !canCreateTask}
                                appearance={!canEditTask && !canCreateTask ? 'outline' : undefined}
                                data-testid='task-dialog-estimated-hours'
                            />
                        </Field>
                    </Section>
                </Column>
                <Column flex='1'>
                    <Section title='Related'>
                        {!taskToEdit && !initialCaseId && (
                            <Field label='Case' required validationMessage={taskForm.errors.caseId}>
                                <CaseDropdown
                                    onCaseSelected={(c) => taskForm.setFieldValue('caseId', c?.caseId)}
                                    caseId={taskForm.values.caseId}
                                    appearance='outline'
                                />
                            </Field>
                        )}
                        {selectMilestone && taskForm.values.caseId && !!milestones.length && (
                            <Field
                                label='Milestone'
                                required={requireMilestone}
                                validationMessage={taskForm.errors.milestoneId}
                            >
                                <MilestoneDropdown
                                    milestones={milestones}
                                    initialMilestoneId={taskForm.values.milestoneId}
                                    onMilestoneSelected={onMilestoneSelected}
                                />
                            </Field>
                        )}
                        {selectMilestone && taskForm.values.caseId && !milestones.length && (
                            <Field
                                label='Milestone'
                                required={requireMilestone}
                                validationState={requireMilestone ? 'error' : 'none'}
                                validationMessage={
                                    requireMilestone
                                        ? 'This account requires tasks to be tied to a milestone. Add a milestone to this case before saving this task.'
                                        : undefined
                                }
                            >
                                <Text size={200} data-testid='task-dialog-no-milestones'>
                                    This case has no milestones.{' '}
                                    <Link onClick={onClickMilestonesLink} data-testid='task-dialog-milestones-link'>
                                        Add a milestone
                                    </Link>
                                </Text>
                            </Field>
                        )}
                    </Section>
                </Column>
            </Row>
        </Column>
    );
};

type NotesTabProps = {
    caseId: string;
    canAddNotes?: boolean;
    notes: Note[];
    onSubmitNote?: (note: Note) => Promise<void>;
};
const NotesTab = ({ caseId, canAddNotes, notes, onSubmitNote }: NotesTabProps) => {

    const sortNotes = (a: Note, b: Note) => {
        const aDate = new Date(a.lifecycle?.createdOn || '').getTime();
        const bDate = new Date(b.lifecycle?.createdOn || '').getTime();
        return bDate - aDate;
    };
    return (
        <Column flex='1' gap='medium' padding='px-3'>
            {!!canAddNotes && (
                <Column width='50%'>
                    <CaseNoteForm caseId={caseId} submitNote={onSubmitNote} title='Add Comment' />
                </Column>
            )}
            <Column flex='1' gap='small'>
                <Text weight='semibold'>Discussion</Text>
                <Column flex='1' gap='medium' padding='py-1' maxHeight='calc(75vh - 25rem)' scroll>
                    {notes?.sort(sortNotes).map((note: Note) => (
                        <NoteControl key={note.noteId} note={note} />
                    ))}
                </Column>
            </Column>
        </Column>
    );
};

enum TimeColumnName {
    date = 'date',
    user = 'user',
    description = 'description',
    billable = 'billable',
    hours = 'hours',
}

type TimeTabProps = {
    entries: TimeTrackingEntry[];
    loading?: boolean;
    hasError?: boolean;
    accountUsers?: UserRef[];
    canEditEntry: (entry: TimeTrackingEntry) => boolean;
    canDeleteEntry: (entry: TimeTrackingEntry) => boolean;
    onModifyEntry: (entry: TimeTrackingEntry) => Promise<void>;
    onDeleteEntry: (entry: TimeTrackingEntry) => Promise<void>;
};
const TimeTab = ({
    entries,
    loading,
    hasError,
    accountUsers = [],
    canEditEntry,
    canDeleteEntry,
    onModifyEntry,
    onDeleteEntry,
}: TimeTabProps) => {
    const utils = useUtilities();
    const { hoursForEntry } = useActiveTimer();

    const [actionEntry, setActionEntry] = useState<TimeTrackingEntry>();
    const [editOpen, setEditOpen] = useState<boolean>(false);
    const [deleteOpen, setDeleteOpen] = useState<boolean>(false);

    const totalHours = entries.reduce((total, entry) => total + hoursForEntry(entry), 0);

    const columns: CustomDataTableColumn<TimeTrackingEntry>[] = [
        {
            id: TimeColumnName.date,
            compare: (a, b) => new Date(a.date || 0).valueOf() - new Date(b.date || 0).valueOf(),
            header: 'Date',
            renderCell: (entry) => <Text>{!!entry.date ? new Date(entry.date).toLocaleDateString() : ''}</Text>,
            defaultWidth: 120,
            minWidth: 100,
        },
        {
            id: TimeColumnName.user,
            compare: (a, b) =>
                utils.getUsersName(a.userId as string, accountUsers).localeCompare(
                    utils.getUsersName(b.userId as string, accountUsers),
                ),
            header: 'User',
            renderCell: (entry) => <Text>{utils.getUsersName(entry.userId as string, accountUsers)}</Text>,
            defaultWidth: 160,
            minWidth: 120,
        },
        {
            id: TimeColumnName.description,
            compare: (a, b) => a.description?.localeCompare(b.description || '') || 0,
            header: 'Description',
            renderCell: (entry) => <Text>{entry.description}</Text>,
            defaultWidth: 320,
            minWidth: 150,
        },
        {
            id: TimeColumnName.billable,
            compare: (a, b) => Number(!!a.isBillable) - Number(!!b.isBillable),
            header: 'Billable',
            renderCell: (entry) => <Text>{!!entry.isBillable ? 'Yes' : 'No'}</Text>,
            defaultWidth: 100,
            minWidth: 80,
        },
        {
            id: TimeColumnName.hours,
            compare: (a, b) => hoursForEntry(a) - hoursForEntry(b),
            header: 'Hours',
            renderCell: (entry) => <Text>{hoursForEntry(entry).toFixed(2)}</Text>,
            justifyContent: 'end',
            defaultWidth: 100,
            minWidth: 80,
        },
    ];

    const EditBundle = bundleIcon(EditFilled, EditRegular);
    const DeleteBundle = bundleIcon(DeleteFilled, DeleteRegular);

    const openEditEntry = (entry: TimeTrackingEntry) => {
        setActionEntry(entry);
        setEditOpen(true);
    };
    const openDeleteEntry = (entry: TimeTrackingEntry) => {
        setActionEntry(entry);
        setDeleteOpen(true);
    };
    const onConfirmDeleteEntry = async (e: any) => {
        e.stopPropagation();

        if (!!actionEntry) {
            await onDeleteEntry(actionEntry);
        }
    };

    const actions: CustomDataTableAction<TimeTrackingEntry>[] = [
        {
            id: 'edit',
            ariaLabel: 'Edit time entry',
            label: 'Edit',
            tooltip: 'Edit',
            icon: <EditBundle />,
            canAccess: (entry) => !!entry && canEditEntry(entry),
            onClick: openEditEntry,
            onMenuClick: openEditEntry,
        },
        {
            id: 'delete',
            ariaLabel: 'Delete time entry',
            label: 'Delete',
            tooltip: 'Delete',
            icon: <DeleteBundle />,
            canAccess: (entry) => !!entry && canDeleteEntry(entry),
            onClick: openDeleteEntry,
            onMenuClick: openDeleteEntry,
        },
    ];

    return (
        <Column flex='1' gap='medium' padding='px-3'>
            <Row>
                <Column vAlign='middle'>
                    <Text size={400} weight='semibold' data-testid='task-dialog-time-total'>
                        Total Tracked Time: {totalHours.toFixed(2)} hrs
                    </Text>
                </Column>
                <Column width='auto' vAlign='middle' align='right'>
                    <Text size={300}>
                        {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
                    </Text>
                </Column>
            </Row>

            {!!loading && (
                <Column align='center' padding='pt-2'>
                    <Spinner size='tiny' label='Loading time entries...' />
                </Column>
            )}

            {!loading && !!hasError && (
                <Column align='center' padding='pt-2'>
                    <Text size={300}>Time entries could not be loaded for this task.</Text>
                </Column>
            )}

            {!loading && !hasError && !entries.length && (
                <Column align='center' padding='pt-2'>
                    <Text size={300} data-testid='task-dialog-time-empty'>
                        No time has been tracked for this task.
                    </Text>
                </Column>
            )}

            {!loading && !hasError && !!entries.length && (
                <Column flex='1'>
                    <CustomDataTable<TimeTrackingEntry>
                        items={entries}
                        rowId='entryId'
                        columns={columns}
                        actions={actions}
                        select={false}
                        showActions={true}
                        bodyHeight='calc(75vh - 25rem)'
                    />
                </Column>
            )}

            <AddEditTimeTrackEntryDialog
                timeTrackEntryToEdit={actionEntry}
                caseId={actionEntry?.caseId}
                entryDate={actionEntry?.date}
                onSubmit={(values) => onModifyEntry(values as TimeTrackingEntry)}
                open={editOpen}
                onOpenChange={(isOpen) => {
                    setEditOpen(isOpen);
                    !isOpen && setActionEntry(undefined);
                }}
            />
            <ConfirmationDialog
                title='Confirm Delete'
                icon={<DeleteRegular />}
                text={`Are you sure you want to delete this time entry (${Number(actionEntry?.hours || 0).toFixed(2)} hrs)?`}
                open={deleteOpen}
                onOpenChange={(isOpen) => {
                    setDeleteOpen(isOpen as boolean);
                    !isOpen && setActionEntry(undefined);
                }}
                onConfirm={onConfirmDeleteEntry}
            />
        </Column>
    );
};

type DocumentsTabProps = {
    caseId?: string;
    taskObj: Task;
    accountId?: string;
    docs: CaseDocument[];
    canEditDocs: boolean;
    canDeleteDocs: boolean;
    canUploadDocs: boolean;
    onDeleteDocs: (docIds: string[]) => Promise<void>;
    onModifyDocument: (doc: CaseDocument) => Promise<void>;
};

const DocumentsTab = (props: DocumentsTabProps) => {
    const { caseId, taskObj, accountId, docs, canEditDocs, canDeleteDocs, canUploadDocs, onDeleteDocs, onModifyDocument } =
        props;

    const toasts = useToasts();
    const tableFeatures = useCustomDataTableFeatures();
    const utils = useUtilities();
    const caseDocActions = useCaseDocumentActions();

    const fileUploadManager: FileUploadManager = FileUploadManager.getInstance(caseDocActions);

    const [selectedDocs, setSelectedDocs] = useState<CaseDocument[]>([]);
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [singleSelect, setSingleSelect] = useState<boolean>(false);

    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const btnClasses = mergeClasses(commonStyles.button.root, styles.documentsBtn);
    const uploadBtnClasses = mergeClasses(commonStyles.button.root, styles.documentsUploadBtn);

    const canUpload = () => {
        return canUploadDocs && !!caseId && !!taskObj.taskId;
    };
    const filterTaskDocs = (doc: CaseDocument) => {
        return utils.lifecycle.filterNotDeleted(doc) && doc.taskId === taskObj.taskId;
    };
    const clearSelectedDocs = () => {
        setSelectedDocs([]);
        tableFeatures.clearSelection();
    };
    const deleteSelectedDocs = async (docsToDelete: CaseDocument[]) => {
        try {
            clearSelectedDocs();

            await onDeleteDocs(docsToDelete.map((d) => d.docId as string));

            if (docsToDelete.length > 1) {
                toasts.caseDocuments.notifyCaseDocsDeleted(docsToDelete);
            } else {
                toasts.caseDocuments.notifyCaseDocDeleted(docsToDelete[0]);
            }
        } catch {
            toasts.caseDocuments.notifyCaseDocDeleteError();
        }
    };
    const describeDropRejection = (rejection: FileRejection) => {
        const reason = rejection.errors
            .map((error) => {
                if (error.code === 'file-too-large') {
                    return `exceeds ${(MAX_FILE_SIZE_BYTES / (1024 * 1024)).toFixed(0)}MB`;
                }
                if (error.code === 'file-invalid-type') {
                    return 'has an unsupported file type';
                }
                if (error.code === 'too-many-files') {
                    return `exceeds the ${MAX_FILES_PER_DROP} files per upload limit`;
                }

                return error.message;
            })
            .join(', ');

        return `${rejection.file.name}: ${reason}`;
    };

    const onSelectedDocsChanged = (selected: CaseDocument[]) => {
        setSelectedDocs(selected);
        setDrawerOpen(selected.length === 1);
    };
    const onSwitchChanged = (ev: any) => {
        ev.stopPropagation();
        setSingleSelect(ev.currentTarget.checked);
    };
    const onClickUpload = (e: any) => {
        e.stopPropagation();
        dropzone.open();
    };
    const onClickDetails = (e: any) => {
        e.stopPropagation();
        setDrawerOpen(true);
    };
    const onConfirmDelete = async (e: any) => {
        e.stopPropagation();
        await deleteSelectedDocs(selectedDocs);
    };
    const onUpdateDoc = async (doc: CaseDocument) => {
        try {
            await onModifyDocument({ ...doc, taskId: taskObj.taskId, caseId });
            toasts.caseDocuments.notifyCaseDocUpdated(doc);
        } catch {
            toasts.caseDocuments.notifyCaseDocUpdatedError(doc);
        }
    };

    const onDrop = (acceptedFiles: File[], fileRejections: FileRejection[]) => {
        if (!taskObj.taskId) {
            toasts.notifyError('Upload unavailable', 'Please save this task before uploading documents.');
            return;
        }

        if (!caseId) {
            toasts.notifyError('Upload unavailable', 'Please select a case before uploading documents.');
            return;
        }

        if (acceptedFiles.length > 0) {
            fileUploadManager.setTaskId(taskObj.taskId);
            fileUploadManager.queueFiles(caseId, acceptedFiles);
        }

        if (fileRejections.length > 0) {
            const rejectionMessages = fileRejections.slice(0, 3).map(describeDropRejection);
            const suffix = fileRejections.length > 3 ? ` (and ${fileRejections.length - 3} more)` : '';

            toasts.notifyWarning('Some files were not added', `${rejectionMessages.join(' | ')}${suffix}`);
        }
    };

    const dropzone = useDropzone({
        onDrop,
        accept: ACCEPTED_DOCUMENT_TYPES,
        maxFiles: MAX_FILES_PER_DROP,
        maxSize: MAX_FILE_SIZE_BYTES,
        noClick: true,
        noKeyboard: true,
    });

    useEffect(() => {
        if (accountId) {
            fileUploadManager.setAccount(accountId);
        }
    }, [accountId]);

    useEffect(() => {
        setSelectedDocs([]);
        tableFeatures.clearSelection();
        setDrawerOpen(false);
        // Only reset selection when switching tasks; tableFeatures reference is not stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskObj.taskId]);

    return (
        <Column flex='1' noGap>
            <Row padding='pl-3'>
                <Column>
                    <Row noGap>
                        <Column width='auto' vAlign='middle' height='auto'>
                            <Label>Selection mode</Label>
                        </Column>
                        <Column width='auto' vAlign='middle' height='auto'>
                            <Switch
                                label='Single-select'
                                checked={singleSelect}
                                onChange={onSwitchChanged}
                                data-testid='task-documents-single-switch'
                            />
                        </Column>
                    </Row>
                </Column>

                <Toolbar>
                    {(canEditDocs || canDeleteDocs) && !!selectedDocs.length && (
                        <>
                            <ToolbarDivider />

                            <Text>{selectedDocs.length} Documents selected</Text>

                            <ToolbarDivider />

                            {!!canEditDocs && selectedDocs.length === 1 && (
                                <Button
                                    className={btnClasses}
                                    aria-label='Details'
                                    appearance='subtle'
                                    shape='circular'
                                    icon={<InfoRegular />}
                                    onClick={onClickDetails}
                                    data-testid='task-documents-details-btn'
                                />
                            )}
                            {!!canDeleteDocs && (
                                <ConfirmationDialog
                                    title='Confirm Delete'
                                    icon={<DeleteRegular />}
                                    text='Are you sure you want to delete the selected documents?'
                                    onConfirm={onConfirmDelete}
                                >
                                    <Button
                                        className={btnClasses}
                                        aria-label='Delete'
                                        icon={<DeleteRegular />}
                                        appearance='subtle'
                                        shape='circular'
                                        data-testid='task-documents-delete-btn'
                                    />
                                </ConfirmationDialog>
                            )}
                        </>
                    )}
                </Toolbar>

                {canUpload() && (
                    <Column width='auto' padding='pr-3' vAlign='middle'>
                        <Tooltip content='Upload documents to this task' relationship='label'>
                            <Button
                                className={uploadBtnClasses}
                                onClick={onClickUpload}
                                icon={<ArrowUpload16Regular />}
                                data-testid='task-documents-upload-btn'
                            >
                                Upload
                            </Button>
                        </Tooltip>
                    </Column>
                )}
            </Row>

            <Column noGap flex='1' overflowX='hidden' overflowY='hidden' className={styles.documentsContentArea}>
                {canUpload() && (
                    <Column
                        flex='1'
                        align='center'
                        vAlign='middle'
                        {...dropzone.getRootProps()}
                        className={mergeClasses(styles.documentsDragArea, dropzone.isDragActive && styles.documentsDragActive)}
                        data-testid='task-documents-drop-zone'
                    >
                        <Text size={900}>Drop Files Here...</Text>
                        <Text size={200}>Supported files only, up to 100MB each and 20 files per upload.</Text>
                        <input {...dropzone.getInputProps()} />
                    </Column>
                )}

                <Row noWrap noGap flex='1'>
                    <DocumentTable
                        docs={docs.filter(filterTaskDocs)}
                        singleSelect={singleSelect}
                        onSelectionChanged={onSelectedDocsChanged}
                        bodyHeight='calc(75vh - 22rem)'
                        onModifyDocument={onUpdateDoc}
                        onDeleteDocs={deleteSelectedDocs}
                        setDrawerOpen={setDrawerOpen}
                        {...tableFeatures.tableProps}
                    />
                    <DocumentDrawer
                        className={styles.documentsDrawer}
                        open={drawerOpen}
                        setOpen={setDrawerOpen}
                        position='end'
                        selectedDoc={docs.find((d) => d.docId == selectedDocs[0]?.docId)}
                        onSubmit={onUpdateDoc}
                    />
                </Row>
            </Column>
        </Column>
    );
};
